import React, { FC } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getGuideForProduct,
  hasPublishedGuide,
  SUPPORT_PORTAL_URL,
} from "../../data/installation-guides";

export interface GuideLinkProps {
  /** Product name as returned by `parseArtomeSerial`, e.g. "M10". */
  product: string | undefined;
  variant?: "inline" | "block";
}

/**
 * The one place that resolves a product to its installation guide.
 *
 * Every guide reference in the app goes through here, which is what guarantees
 * the "no guide published" case is handled. One product hits it today: S10PPS,
 * which is not in the catalogue at all. A listed category whose guide Artome has
 * not published yet hits the same fallback — X12 did until 2026-11 — as does any
 * further product, rather than rendering a dead link.
 */
const GuideLink: FC<GuideLinkProps> = ({ product, variant = "inline" }) => {
  const guide = getGuideForProduct(product);

  if (!hasPublishedGuide(guide)) {
    return (
      <Box>
        <Typography variant="body2">
          No installation guide is published for this product yet.{" "}
          <Link
            href={SUPPORT_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 700 }}
          >
            Contact Artome support
          </Link>
          .
        </Typography>
      </Box>
    );
  }

  return (
    <Link
      href={guide.guideUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open the Artome ${guide.title} installation guide`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontWeight: 700,
        fontSize: variant === "block" ? 16 : 14,
        color: "text.primary",
        textDecorationColor: "primary.main",
      }}
    >
      {guide.title} installation guide
      <OpenInNewIcon sx={{ fontSize: 15 }} />
    </Link>
  );
};

export default GuideLink;
